<template>
  <v-row class="pt-0 pb-10 px-2" justify="center">
    <v-col
      v-for="(item, key) in statusBlocks"
      cols="12"
      sm="6"
      md="3"
      :key="`statusblock-${key}`"
    >
      <v-row>
        <v-col cols="12">
          <h3 class="title primary--text text-center mb-1">
            {{ $t(item.title) }}
          </h3>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-progress-circular
          class="hide-in-percy"
          :rotate="360"
          :size="130"
          :width="10"
          :value="item.value"
          color="cpuChartLine"
        >
          <span class="f-30px hide-in-percy"> {{ item.value }}% </span>
        </v-progress-circular>
      </v-row>
    </v-col>

    <v-col
      class="d-flex"
      v-for="(item, key) in topBlocks"
      cols="12"
      sm="6"
      md="6"
      lg="3"
      :key="`topblock-${key}`"
    >
      <v-card
        :id="item.id"
        outlined
        :color="item.cardColor"
        dark
        flat
        :to="item.link"
        :tile="$vuetify.breakpoint.xsOnly"
        class="flex-grow-1 rounded-lg"
        @click="changeToSession(item.id)"
      >
        <v-row align="center" no-gutters>
          <v-col cols="12" class="pl-2">
            <v-card-title
              v-if="item.id === 'uptime_block'"
              :class="item.textSize"
              class="font-weight-regular pl-0 pr-1 pb-2"
            >
              <span>
                {{ item.value[0].replace(" ", '') }}
              </span>
              <span v-if="item.value.length > 1">&nbsp; </span>

              <span v-if="item.value.length > 1">
                {{ item.value[1].replace(" ", '') }}
              </span>
              <span v-if="item.value.length > 2">&nbsp; </span>

              <span v-if="item.value.length > 2">
                {{ item.value[2].replace(" ", '') }}
              </span>
              <span v-if="item.value.length > 3">&nbsp; </span>

              <span v-if="item.value.length > 3">
                {{ item.value[3].replace(" ", '') }}
              </span>
              <span v-if="item.value.length > 4">&nbsp; </span>

              <span v-if="item.value.length > 4">
                {{ item.value[4].replace(" ", '') }}
              </span>
              <span v-if="item.value.length > 5">&nbsp; </span>

              <span v-if="item.value.length > 5">
                {{ item.value[5].replace(" ", '') }}
              </span>
            </v-card-title>

            <v-card-title
              v-else
              :class="item.textSize"
              class="font-weight-regular pl-0 pr-1 pb-2"
            >
              {{ item.value }}
            </v-card-title>

            <v-card-text class="pl-0 pb-1">
              <v-row no-gutters align="center" class="mr-2 pb-2">
                <v-col cols="7">
                  <div
                    class="subtitle-1 font-weight-regular"
                  >
                    {{ $tc(item.title, 2) }}
                  </div>
                </v-col>

                <v-col cols="4">
                  <div
                    class="pt-0 pl-0 pr-0 body-2 font-weight-light"
                  >
                    {{ $t(item.hint) }}
                  </div>
                </v-col>

                <v-col cols="1">
                  <v-icon large color="white">
                    {{ item.icon }}
                  </v-icon>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-text class="pt-2 pb-2 pl-0 pr-2">
              <div
                v-if="item.progressLine === null"
              />

              <v-progress-linear
                v-else
                :rotate="360"
                :size="130"
                height="5"
                :value="item.progressLine"
                color="white"
              />
            </v-card-text>
          </v-col>
        </v-row>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import HelperMixin from '@/mixins/helper'

export default {
  mixins: [HelperMixin],
  created () {
    this.$store.dispatch('hsm/getInfo')
    this.$store.dispatch('hsm/getLogSize')
  },
  mounted () {
    this.setIntervalId = setInterval(() => {
      this.$store.dispatch('hsm/getInfo')
      this.$store.dispatch('hsm/getLogSize')
    }, this.requisitionInterval)
  },
  data () {
    return {
      requisitionInterval: 2000,
      setIntervalId: null
    }
  },
  computed: {
    topBlocks () {
      if (this.info) {
        const partitions = this.info.usr_count
        const objects = this.info.obj_count
        const nodes = this.info.nodes.length
        const sessions = this.info.session
        const dbSize = this.$options.filters.prettyBytes(this.info.sl_be_len)
        const syncPoint = this.info.sync_point
        const telemetry = this.info.ds_bound ? this.$t('yes') : this.$t('no')
        const logSize = this.$options.filters.prettyBytes(this.logSize)
        const remoot = this.info.remoot ? this.$t('yes') : this.$t('no')
        let uptime = this.getElapsedTimeString({
          secondsInterval: this.info.uptime
        })
        const firmwareVersion = this.info.firmwareVersion

        uptime = this.replaceWordsWithInitials(uptime)

        const arrayUptime = uptime.split(', ')

        let mode
        const modeData = this.info.hsm_mode
        switch (modeData) {
          case 0:
            mode = 'NRM'
            break
          case 1:
            mode = 'RM1'
            break
          case 2:
            mode = 'RM2'
            break
          default:
            mode = 'unknown'
        }

        return [
          {
            icon: 'group',
            title: 'partition',
            hint: 'partition_hint',
            value: partitions,
            link: { name: 'users-management' },
            id: 'users_management_block',
            textSize: 'text-h5 font-weight-bold',
            color: 'secondary',
            dark: true,
            cardColor: this.getCardColor(this.percentPartitionSize(partitions)),
            progressLine: this.percentPartitionSize(partitions)
          },
          {
            icon: 'vpn_key',
            title: 'object',
            hint: 'object_hint',
            value: objects,
            link: { name: 'hsm-info', query: { tab: 'status' } },
            id: 'partition_keys_block',
            textSize: 'text-h5 font-weight-bold',
            color: 'secondary',
            dark: true,
            cardColor: this.getCardColor(this.percentObjectsSize(objects)),
            progressLine: this.percentObjectsSize(objects)
          },
          {
            icon: 'account_tree',
            title: 'session',
            hint: 'session_hint',
            value: sessions,
            id: 'sessions_block',
            textSize: 'text-h5 font-weight-bold',
            color: 'secondary',
            dark: true,
            cardColor: this.getCardColor(this.percentSessionSize(sessions)),
            progressLine: this.percentSessionSize(sessions)
          },
          {
            icon: 'today',
            title: 'uptime',
            hint: 'uptime_hint',
            value: arrayUptime,
            link: { name: 'hsm-info', query: { tab: 'status' } },
            id: 'uptime_block',
            textSize: 'text-h5 font-weight-bold',
            color: 'secondary',
            dark: true,
            cardColor: 'green',
            progressLine: null
          },
          // #########  2 Line  ###########
          {
            icon: 'mdi-database-outline',
            title: 'db_size',
            hint: 'db_size_hint',
            value: dbSize,
            link: { name: 'hsm-info', query: { tab: 'status' } },
            id: 'db_size_block',
            textSize: 'text-h5 font-weight-bold',
            color: 'background',
            dark: false,
            cardColor: this.getCardColor(this.percentDbSize(this.info.sl_be_len)),
            progressLine: this.percentDbSize(this.info.sl_be_len)
          },
          {
            icon: 'format_align_left',
            title: 'log_size',
            hint: 'log_size_hint',
            value: logSize,
            link: { name: 'hsm-log' },
            id: 'log_size_block',
            textSize: 'text-h5 font-weight-bold',
            // color: 'secondary',
            dark: false,
            cardColor: this.getCardColor(this.percentLogSize(this.logSize).toFixed(2)),
            progressLine: this.percentLogSize(this.logSize).toFixed(2)
          },
          {
            icon: 'device_hub',
            title: 'node',
            hint: 'node_hint',
            value: nodes,
            link: { name: 'hsm-replication' },
            id: 'hsm_replication_block',
            textSize: 'text-h5 font-weight-bold',
            // color: 'secondary',
            dark: false,
            cardColor: this.getNodeColor(this.percentNodesSize(nodes)),
            progressLine: this.percentNodesSize(nodes)
          },
          {
            icon: 'sync',
            title: 'sync_point',
            hint: 'sync_point_hint',
            value: syncPoint,
            link: { name: 'hsm-info', query: { tab: 'replication' } },
            id: 'sync_point_block',
            textSize: 'text-h5 font-weight-bold',
            // color: 'secondary',
            dark: false,
            cardColor: 'green',
            progressLine: null
          },
          // #########  3 Line  ###########
          {
            icon: 'group_work',
            title: 'operation_mode',
            hint: 'operation_mode_hint',
            value: mode,
            link: { name: 'hsm-info', query: { tab: 'status' } },
            id: 'monitor_sessions_block',
            hideFromPercy: true,
            textSize: 'text-h5 font-weight-bold',
            color: 'secondary',
            dark: true,
            cardColor: 'blue',
            progressLine: null
          },
          {
            icon: 'memory',
            title: 'firmware_version',
            hint: 'firmware_version_hint',
            value: firmwareVersion,
            link: { name: 'hsm-info' },
            id: 'firmware_version_block',
            textSize: 'text-h5 font-weight-bold',
            color: 'secondary',
            dark: true,
            cardColor: 'blue',
            progressLine: null
          },
          {
            icon: 'settings_remote',
            title: 'remoot',
            hint: 'remoot_hint',
            value: remoot,
            link: { name: 'hsm-info', query: { tab: 'status' } },
            id: 'remoot_block',
            textSize: 'text-h5 font-weight-bold',
            color: 'secondary',
            dark: true,
            cardColor: 'blue',
            progressLine: null
          },
          {
            icon: 'cloud',
            title: 'bind_dinamo_services',
            hint: 'telemetry_hint',
            value: telemetry,
            link: { name: 'hsm-settings', params: { tab: 1 } },
            id: 'telemetry_block',
            textSize: 'text-h5 font-weight-bold',
            color: 'secondary',
            dark: true,
            cardColor: 'blue',
            progressLine: null
          }
        ]
      }

      return []
    },
    logSize () {
      return this.$store.state.hsm.logSize
    },
    info () {
      return this.$store.getters['hsm/GET_INFO']
    },
    statusBlocks () {
      if (this.info) {
        const cpu = this.info.cpu
        const ram = this.info.mem_used
        const disk = Math.floor(
          (1 - this.info.free_blocks / this.info.total_blocks) * 100
        )
        const logSize = this.percentLogSize(this.logSize).toFixed(2)

        return [
          {
            title: 'processing',
            value: cpu
          },
          {
            title: 'memory',
            value: ram
          },
          {
            title: 'storage',
            value: disk
          },
          {
            title: 'log',
            value: logSize
          }
        ]
      }
      return []
    },
    locale () {
      return this.$store.state.locale.locale
    }
  },
  methods: {
    changeToSession (id) {
      if (id === 'sessions_block') {
        this.$emit('tab', 2)
      }
    },
    percentLogSize (bytes) {
      const totalMemory = 4 * 1024 * 1024 * 1024 // 4GB in bytes
      return (bytes / totalMemory) * 100
    },
    replaceWordsWithInitials (str) {
      return str.replace(/Years|Anos|Meses|Months|Dias|Days|Horas|Hours|Minutos|Minutes|Segundos|Seconds/gi, function (matched) {
        return matched[0].toLowerCase()
      })
    },
    getCardColor (valuePercent) {
      switch (this.info.model) {
        case 'DMP':
          if (valuePercent < 75) {
            return 'green'
          } else if (valuePercent < 90) {
            return 'orange'
          } else {
            return 'red'
          }

        case 'DCD':
          if (valuePercent < 75) {
            return 'green'
          } else if (valuePercent < 90) {
            return 'orange'
          } else {
            return 'red'
          }

        case 'DXP':
          if (valuePercent < 75) {
            return 'green'
          } else if (valuePercent < 90) {
            return 'yellow'
          } else {
            return 'red'
          }

        case 'DST':
          if (valuePercent < 75) {
            return 'green'
          } else if (valuePercent < 90) {
            return 'yellow'
          } else {
            return 'red'
          }

        default:
          break
      }
    },
    percentDbSize (dbSizeBytes) {
      const dbMaxSizeDMP = 819200
      const dbMaxSizeDCD = 20971520
      const dbMaxSizeDXP = 838860800
      const dbMaxSizeDST = 2147483648

      switch (this.info.model) {
        case 'DMP':
          return (dbSizeBytes * 100) / dbMaxSizeDMP

        case 'DCD':
          return (dbSizeBytes * 100) / dbMaxSizeDCD

        case 'DXP':
          return (dbSizeBytes * 100) / dbMaxSizeDXP

        case 'DST':
          return (dbSizeBytes * 100) / dbMaxSizeDST

        default:
          break
      }
    },
    percentSessionSize (session) {
      const maxSessionDMP = 12
      const maxSessionDCD = 1500
      const maxSessionDXP = 3000
      const maxSessionDST = 4000

      switch (this.info.model) {
        case 'DMP':
          return (session * 100) / maxSessionDMP

        case 'DCD':
          return (session * 100) / maxSessionDCD

        case 'DXP':
          return (session * 100) / maxSessionDXP

        case 'DST':
          return (session * 100) / maxSessionDST

        default:
          break
      }
    },
    percentPartitionSize (partitions) {
      const maxPartitionsDMP = 200
      const maxPartitionsDCD = 10000
      const maxPartitionsDXP = 400000
      const maxPartitionsDST = 1000000

      switch (this.info.model) {
        case 'DMP':
          return (partitions * 100) / maxPartitionsDMP

        case 'DCD':
          return (partitions * 100) / maxPartitionsDCD

        case 'DXP':
          return (partitions * 100) / maxPartitionsDXP

        case 'DST':
          return (partitions * 100) / maxPartitionsDST

        default:
          break
      }
    },
    percentObjectsSize (objects) {
      const maxObjectsDMP = 200
      const maxObjectsDCD = 10000
      const maxObjectsDXP = 400000
      const maxObjectsDST = 1000000

      switch (this.info.model) {
        case 'DMP':
          return (objects * 100) / maxObjectsDMP

        case 'DCD':
          return (objects * 100) / maxObjectsDCD

        case 'DXP':
          return (objects * 100) / maxObjectsDXP

        case 'DST':
          return (objects * 100) / maxObjectsDST

        default:
          break
      }
    },
    getNodeColor (nodesPercent) {
      if (nodesPercent < 100) {
        return 'green'
      } else {
        return 'red'
      }
    },
    percentNodesSize (nodes) {
      const maxNodes = 16

      return (nodes * 100) / maxNodes
    }

  },
  destroyed () {
    clearInterval(this.setIntervalId)
  }
}
</script>

<style>
.my-card {
  outline: 2px solid;
}
</style>
